import ApplicationController from './application_controller'

// EN/TR pill on LexiQamus search header and sidebar (CPG-397).
// Locale change without full reload: fetch HTML with ?locale=, swap translated regions, update history.
export default class extends ApplicationController {
  // Guard concurrent locale fetches (header + sidebar both fire on one click).
  static localeFetchInProgress = false
  // One window listener, bound to this class (an unbound static method would see `this` as window).
  static boundPopstateHandler = null
  static connectedSwitchCount = 0

  connect() {
    this.handleLanguageSwitchClick = this.handleLanguageSwitchClick.bind(this)
    this.element.addEventListener('click', this.handleLanguageSwitchClick)
    this.constructor.connectedSwitchCount += 1
    this.constructor.ensurePopstateHandler()
  }

  disconnect() {
    this.element.removeEventListener('click', this.handleLanguageSwitchClick)
    this.constructor.connectedSwitchCount -= 1
    if (this.constructor.connectedSwitchCount <= 0) {
      this.constructor.connectedSwitchCount = 0
      this.constructor.removePopstateHandler()
    }
  }

  // Delegate clicks to inactive locale links only (active locale is a non-clickable span).
  handleLanguageSwitchClick(clickEvent) {
    const clickedLocaleLink = clickEvent.target.closest(
      '.lq-lang-switch-option--en, .lq-lang-switch-option--tr'
    )
    if (!clickedLocaleLink || clickedLocaleLink.tagName !== 'A' || !this.element.contains(clickedLocaleLink)) {
      return
    }

    clickEvent.preventDefault()
    if (this.constructor.localeFetchInProgress) return

    const targetLocaleCode = clickedLocaleLink.dataset.locale
    if (!targetLocaleCode || (targetLocaleCode !== 'en' && targetLocaleCode !== 'tr')) return

    const localeSwitchUrl = clickedLocaleLink.getAttribute('href')
    if (!localeSwitchUrl) return

    const turkishLocaleSelected = targetLocaleCode === 'tr'
    // Immediate visual feedback before fetch completes.
    this.constructor.syncThumbAcrossAllSwitches(turkishLocaleSelected)
    this.constructor.fetchAndSwapLocale(localeSwitchUrl)
  }

  static ensurePopstateHandler() {
    if (this.boundPopstateHandler) return

    this.boundPopstateHandler = () => {
      this.fetchAndSwapLocale(window.location.href, { fromPopstate: true })
    }
    window.addEventListener('popstate', this.boundPopstateHandler)
  }

  static removePopstateHandler() {
    if (!this.boundPopstateHandler) return

    window.removeEventListener('popstate', this.boundPopstateHandler)
    this.boundPopstateHandler = null
  }

  // Slide the thumb (navy EN / bordo TR) on every switch on the page in sync.
  static syncThumbAcrossAllSwitches(turkishLocaleSelected) {
    document.querySelectorAll('.lq-lang-switch').forEach((languageSwitchRoot) => {
      languageSwitchRoot.classList.toggle('lq-lang-switch--tr-active', turkishLocaleSelected)
    })
  }

  // Fetch the same path with updated locale; on failure fall back to a full navigation.
  // fromPopstate: the address bar already moved, so do not push another history entry.
  static async fetchAndSwapLocale(localeSwitchUrl, { fromPopstate = false } = {}) {
    if (this.localeFetchInProgress) return
    this.localeFetchInProgress = true

    try {
      const localePageResponse = await fetch(localeSwitchUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      if (!localePageResponse.ok) {
        throw new Error(`Locale fetch failed: ${localePageResponse.status}`)
      }

      const fetchedHtml = await localePageResponse.text()
      this.applyFetchedLocaleDocument(fetchedHtml)
      if (!fromPopstate) {
        window.history.pushState({ lexiqamusLocaleNavigation: true }, '', localeSwitchUrl)
      }
      this.reinitializeAfterLocaleSwap()
    } catch (_fetchError) {
      window.location.assign(localeSwitchUrl)
    } finally {
      this.localeFetchInProgress = false
    }
  }

  // Parse fetched document and replace locale-sensitive regions (server-rendered I18n matches full reload).
  static applyFetchedLocaleDocument(fetchedHtml) {
    const fetchedDocument = new DOMParser().parseFromString(fetchedHtml, 'text/html')

    if (fetchedDocument.title) document.title = fetchedDocument.title

    const htmlLangAttribute = fetchedDocument.documentElement.getAttribute('lang')
    if (htmlLangAttribute) document.documentElement.setAttribute('lang', htmlLangAttribute)

    this.replaceLiveRegionFromFetch(fetchedDocument, 'main.lexiqamus-main-content', 'main.lexiqamus-main-content')
    this.replaceLiveRegionFromFetch(fetchedDocument, '.lexiqamus-header-content', '.lexiqamus-header-content')
    this.replaceLiveRegionFromFetch(
      fetchedDocument,
      '.lexiqamus-sidebar-panel-content',
      '.lexiqamus-sidebar-panel-content'
    )
    this.replaceLiveRegionFromFetch(fetchedDocument, '#js-flash-messages', '#js-flash-messages')
    this.replaceLiveRegionFromFetch(fetchedDocument, '.lexiqamus-site-footer', '.lexiqamus-site-footer')
    this.replaceLiveRegionFromFetch(fetchedDocument, '#lq-locale-access-footer', '#lq-locale-access-footer')
    this.replaceLiveRegionFromFetch(fetchedDocument, '#lq-locale-common-modals', '#lq-locale-common-modals')
    this.replaceLiveRegionFromFetch(fetchedDocument, '#lq-locale-sign-in', '#lq-locale-sign-in')
  }

  // Copy innerHTML from fetched fragment into the live page (Stimulus reconnects on new nodes).
  static replaceLiveRegionFromFetch(fetchedDocument, fetchedRegionSelector, liveRegionSelector) {
    const fetchedRegion = fetchedDocument.querySelector(fetchedRegionSelector)
    const liveRegion = document.querySelector(liveRegionSelector)
    if (!fetchedRegion || !liveRegion) return
    liveRegion.innerHTML = fetchedRegion.innerHTML
  }

  // Icons and tooltips only. A synthetic turbolinks:load also re-inits announcements and flashes.
  static reinitializeAfterLocaleSwap() {
    if (typeof feather !== 'undefined') feather.replace()

    if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
      const tooltipSelector =
        '.lexiqamus-main-content [data-bs-toggle="tooltip"], ' +
        '.lexiqamus-sidebar-panel [data-bs-toggle="tooltip"], ' +
        '.lq-lang-switch [data-bs-toggle="tooltip"]'

      document.querySelectorAll(tooltipSelector).forEach((tooltipElement) => {
        bootstrap.Tooltip.getInstance(tooltipElement)?.dispose()
        new bootstrap.Tooltip(tooltipElement)
      })
    }

    this.rebindPreviewModalClose()
  }

  // common_modals ships a document-ready handler that does not survive innerHTML replacement.
  static rebindPreviewModalClose() {
    const previewModal = document.getElementById('js-preview-modal')
    if (!previewModal || typeof window.$ !== 'function') return

    window.$(previewModal).off('hidden.bs.modal.lqLocale').on('hidden.bs.modal.lqLocale', () => {
      if (typeof cropper !== 'undefined') cropper.closePreviewModal()
    })
  }
}
